/**
 * Finding the math in a tutor reply, so both chat front ends render the same spans.
 *
 * The model writes LaTeX in four delimiter styles — $…$, $$…$$, \(…\), \[…\] — and
 * markdown parsers mangle every one of them (backslash escapes eat \( and \[, and
 * _ and * inside TeX turn into emphasis). So math is found here, BEFORE markdown runs,
 * and rewritten into one canonical form each renderer handles unambiguously:
 *
 *   inline  → $$tex$$            (on one line, inside the surrounding text)
 *   display → a block of its own:  \n\n$$\ntex\n$$\n\n
 *
 * The web chat feeds that to remark-math with single-dollar math turned off; the
 * widget's marked extension reads the same two forms.
 *
 * Single dollars follow Pandoc's rule, which is what keeps prices from becoming
 * math: a $ opens only if a non-space follows it, closes only if a non-space precedes
 * it and no digit follows, and the span holds no other $. "It costs $5 and $10" stays
 * text; "$x^2$" is math. Code (fenced blocks and `inline spans`) is never touched.
 */

const FENCE = /^ {0,3}(`{3,}|~{3,})/;

/**
 * Display math first, so $$…$$ is never read as two single-dollar spans. The single-
 * dollar branch captures the character before the $ (group 4) instead of using a
 * lookbehind: Safari before 16.4 cannot parse lookbehind, and in the widget's single
 * bundle that would take down the whole widget, not just math.
 */
const MATH =
  /\$\$([\s\S]+?)\$\$|\\\[([\s\S]+?)\\\]|\\\(([\s\S]+?)\\\)|(^|[^\\$\w])\$(?=[^\s$])([^$\n]*?[^\s$\\])\$(?![\d$])/g;

/** Inline code spans: a run of backticks, then anything up to the same-length run. */
const CODE_SPAN = /(`+)([\s\S]*?[^`])\1(?!`)/g;

function rewriteMath(text: string): string {
  return text.replace(MATH, (whole, ddollar, bracket, paren, before, single) => {
    const display = ddollar ?? bracket;
    const tex = (display ?? paren ?? single ?? "").trim();
    if (!tex || tex.includes("$$")) return whole;
    if (display !== undefined) return `\n\n$$\n${tex}\n$$\n\n`;
    // Inline TeX spanning lines would be split by the markdown parser; join it.
    return `${before ?? ""}$$${tex.replace(/\s*\n\s*/g, " ")}$$`;
  });
}

/** Rewrite prose (no fences) while leaving inline code spans exactly as written. */
function rewriteProse(text: string): string {
  let out = "";
  let last = 0;
  for (const m of text.matchAll(CODE_SPAN)) {
    out += rewriteMath(text.slice(last, m.index)) + m[0];
    last = (m.index ?? 0) + m[0].length;
  }
  return out + rewriteMath(text.slice(last));
}

/** Rewrite every math span in a markdown string into the canonical $$ forms. */
export function normalizeMath(markdown: string): string {
  // Cheap exit: no delimiter characters at all, nothing to do (most replies).
  if (!/[$\\]/.test(markdown)) return markdown;

  const lines = markdown.split("\n");
  const out: string[] = [];
  let prose: string[] = [];
  let fence: string | null = null;

  const flush = () => {
    if (prose.length) out.push(rewriteProse(prose.join("\n")));
    prose = [];
  };

  for (const line of lines) {
    const m = line.match(FENCE);
    if (fence) {
      out.push(line);
      // A closing fence is the same character, at least as long, and nothing after it.
      if (m && m[1][0] === fence[0] && m[1].length >= fence.length && !line.slice(m[0].length).trim()) {
        fence = null;
      }
    } else if (m) {
      flush();
      fence = m[1];
      out.push(line);
    } else {
      prose.push(line);
    }
  }
  flush();
  return out.join("\n");
}
